import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useMobileMenu } from '../hooks/useMobileMenu';
import { useAds } from '../hooks/useAds';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Pencil, Power, PowerOff, LayoutGrid, TrendingUp, Eye, PlusCircle, Trash2, CheckCircle2 } from 'lucide-react';
import DeleteAdModal from '../components/DeleteAdModal';

export default function MyAdsPage(): JSX.Element {
  const { mobileOpen, toggleMobile } = useMobileMenu();
  const { myAds, fetchMyAds, toggleActive, deleteAd, markAsSold, isLoading } = useAds();
  const navigate = useNavigate();
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);

  useEffect(() => {
    fetchMyAds();
  }, [fetchMyAds]);

  const activeCount = myAds.filter(ad => ad.is_active).length;
  const inactiveCount = myAds.length - activeCount;

  const statusConfig: Record<string, { label: string; classes: string }> = {
    approved: { label: 'Approved', classes: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' },
    pending:  { label: 'Pending',  classes: 'bg-amber-50  text-amber-700  ring-1 ring-amber-200'  },
    rejected: { label: 'Rejected', classes: 'bg-red-50    text-red-700    ring-1 ring-red-200'    },
  };

  return (
    <>
      <Helmet>
        <title>My Ads | AdsWorld</title>
      </Helmet>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Serif+Display&display=swap');

        .ads-root { font-family: 'DM Sans', sans-serif; }

        /* card hover lift */
        .ad-card {
          transition: box-shadow 0.2s ease, transform 0.2s ease;
        }
        .ad-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px -4px rgba(0,0,0,0.08), 0 2px 8px -2px rgba(0,0,0,0.04);
        }

        /* skeleton shimmer */
        @keyframes shimmer {
          0%   { background-position: -600px 0; }
          100% { background-position:  600px 0; }
        }
        .skeleton {
          background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
          background-size: 600px 100%;
          animation: shimmer 1.4s infinite;
          border-radius: 8px;
        }

        /* button press */
        .btn-action { transition: background 0.15s, transform 0.1s; }
        .btn-action:active { transform: scale(0.97); }

        /* stat card */
        .stat-card { transition: box-shadow 0.2s; }
        .stat-card:hover { box-shadow: 0 4px 16px -4px rgba(0,0,0,0.1); }
      `}</style>

      <div className="ads-root min-h-screen bg-gray-50">
        <Navbar mobileOpen={mobileOpen} onToggleMobile={toggleMobile} />

        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">

          {/* ── Breadcrumb ── */}
          <nav className="flex items-center gap-1.5 text-sm text-gray-400 mb-8">
            <span
              className="hover:text-primary-600 cursor-pointer transition-colors"
              onClick={() => navigate('/')}
            >Home</span>
            <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
            <span
              className="hover:text-primary-600 cursor-pointer transition-colors"
              onClick={() => navigate('/profile')}
            >Profile</span>
            <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
            <span className="text-gray-700 font-medium">My Ads</span>
          </nav>

          {/* ── Page Header ── */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase text-primary-500 mb-1">
                Your Posts
              </p>
              <h1
                className="text-3xl font-bold text-gray-900"
                style={{ fontFamily: "'DM Serif Display', serif" }}
              >
                My Ads
              </h1>
            </div>
            <button
              onClick={() => navigate('/post-ad')}
              className="btn-action inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold shadow-sm"
            >
              <PlusCircle className="w-4 h-4" />
              Post New Ad
            </button>
          </div>

          {/* ── Stats Strip ── */}
          {!isLoading && myAds.length > 0 && (
            <div className="grid grid-cols-3 gap-3 mb-8">
              {[
                { icon: <LayoutGrid className="w-4 h-4" />, label: 'Total',    value: myAds.length,  color: 'text-gray-700',   bg: 'bg-gray-100'   },
                { icon: <TrendingUp  className="w-4 h-4" />, label: 'Active',   value: activeCount,   color: 'text-emerald-700', bg: 'bg-emerald-50' },
                { icon: <Eye         className="w-4 h-4" />, label: 'Inactive', value: inactiveCount, color: 'text-amber-700',  bg: 'bg-amber-50'   },
              ].map(stat => (
                <div
                  key={stat.label}
                  className="stat-card bg-white rounded-2xl px-4 py-4 border border-gray-100 flex items-center gap-3"
                >
                  <span className={`${stat.bg} ${stat.color} p-2 rounded-lg`}>{stat.icon}</span>
                  <div>
                    <p className="text-2xl font-bold text-gray-900 leading-none">{stat.value}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Content ── */}
          {isLoading ? (
            /* Skeleton */
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-2xl p-4 border border-gray-100 flex items-center gap-4">
                  <div className="skeleton w-20 h-20 rounded-xl flex-shrink-0" />
                  <div className="flex-1 space-y-2.5">
                    <div className="skeleton h-4 w-3/5 rounded" />
                    <div className="skeleton h-3 w-2/5 rounded" />
                    <div className="skeleton h-3 w-1/4 rounded" />
                  </div>
                  <div className="flex gap-2">
                    <div className="skeleton w-20 h-9 rounded-lg" />
                    <div className="skeleton w-28 h-9 rounded-lg" />
                  </div>
                </div>
              ))}
            </div>

          ) : myAds.length === 0 ? (
            /* Empty State */
            <div className="bg-white rounded-2xl border border-dashed border-gray-200 py-16 flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                <LayoutGrid className="w-6 h-6 text-gray-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-800 mb-1">No ads yet</h3>
              <p className="text-sm text-gray-400 mb-5">Start by posting your first listing.</p>
              <button
                onClick={() => navigate('/post-ad')}
                className="btn-action inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold"
              >
                <PlusCircle className="w-4 h-4" /> Post an Ad
              </button>
            </div>

          ) : (
            /* Ads List */
            <div className="space-y-3">
              {myAds.map((ad, idx) => {
                const key = (ad.status as unknown as string) || '';
                const statusInfo = (statusConfig as any)[key] ?? {
                  label: ad.status,
                  classes: 'bg-gray-100 text-gray-600 ring-1 ring-gray-200',
                };

                return (
                  <div
                    key={ad.id}
                    className="ad-card bg-white rounded-2xl border border-gray-100 overflow-hidden"
                    style={{ animationDelay: `${idx * 50}ms` }}
                  >
                    <div className="flex items-center gap-4 p-4">

                      {/* Thumbnail */}
                      <div className="relative flex-shrink-0">
                        <img
                          src={ad.primary_image || '/assets/placeholder.jpg'}
                          alt={ad.title}
                          className="w-20 h-20 rounded-xl object-cover bg-gray-100"
                        />
                        {!ad.is_active && (
                          <div className="absolute inset-0 rounded-xl bg-white/60 backdrop-blur-[1px]" />
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2 flex-wrap">
                          <h3 className="font-semibold text-gray-900 text-sm leading-snug truncate">
                            {ad.title}
                          </h3>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusInfo.classes}`}>
                            {statusInfo.label}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {ad.category_name}
                          <span className="mx-1.5 text-gray-200">•</span>
                          {new Date(ad.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                        <p className="text-sm font-bold text-gray-900 mt-1.5">
                          <span className="text-xs font-semibold text-gray-400 mr-0.5">PKR</span>
                          {Number(ad.price).toLocaleString()}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => navigate(`/item/${ad.id}`)}
                          className="btn-action inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-600 text-xs font-medium"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">View</span>
                        </button>
                        <button
                          onClick={() => navigate(`/post-ad?edit=${ad.id}`)}
                          className="btn-action inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-600 text-xs font-medium"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Edit</span>
                        </button>
                        <button
                          onClick={() => toggleActive(ad.id)}
                          className={`btn-action inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold ${
                            ad.is_active
                              ? 'bg-red-50 text-red-600 hover:bg-red-100 ring-1 ring-red-100'
                              : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 ring-1 ring-emerald-100'
                          }`}
                        >
                          {ad.is_active
                            ? <><PowerOff className="w-3.5 h-3.5" /><span className="hidden sm:inline">Deactivate</span></>
                            : <><Power     className="w-3.5 h-3.5" /><span className="hidden sm:inline">Activate</span></>
                          }
                        </button>
                        {ad.status !== 'sold' && (
                          <button
                            onClick={() => markAsSold(ad.id)}
                            className="btn-action inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 ring-1 ring-blue-100"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Mark Sold</span>
                          </button>
                        )}
                        <button
                          onClick={() => setDeleteTarget({ id: ad.id, title: ad.title })}
                          className="btn-action inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-red-50 text-red-700 hover:bg-red-100 ring-1 ring-red-100"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Delete</span>
                        </button>
                      </div>

                    </div>

                    {/* Bottom accent bar for inactive ads */}
                    {!ad.is_active && (
                      <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-amber-200 to-transparent" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </main>

        <DeleteAdModal
          isOpen={!!deleteTarget}
          adTitle={deleteTarget?.title}
          onClose={() => setDeleteTarget(null)}
          onConfirm={async () => {
            if (deleteTarget) {
              await deleteAd(deleteTarget.id);
            }
          }}
        />

        <Footer onOpenPrivacySettings={() => {}} />
      </div>
    </>
  );
}
